import { useMemo, useState } from 'react';
import { useBrandsOverviewQuery } from '../../queries/brandMetrics';
import { formatMetric } from './panelConfig';
import SortHeader from './components/SortHeader';
import BrandLogo from './components/BrandLogo';
import './BrandsOverviewTable.css';

const fmtMoney = (v, cur) => (v == null ? '—' : `${cur || ''} ${formatMetric(v, 'number')}`.trim());

// Semáforo de % vs objetivo: texto en negrita de color (sin fondo).
const pctClass = (pct) => {
  if (pct == null) return 'muted';
  if (pct >= 100) return 'ok';
  if (pct >= 80) return 'warn';
  return 'bad';
};

// YoY: crecimiento (verde) o caída (rojo) de la venta/leads vs el mismo rango del año anterior.
const yoyClass = (pct) => (pct == null ? 'muted' : pct >= 0 ? 'ok' : 'bad');

// Semáforo de % de marketing: real vs objetivo, banda de ±5% relativo al objetivo.
// En objetivo (verde) · gastando de menos (ámbar) · pasado de budget (rojo).
const MKT_TOLERANCE = 0.05;

const mktClass = (real, target) => {
  if (real == null || target == null) return real == null ? 'muted' : 'ok';
  if (real < target * (1 - MKT_TOLERANCE)) return 'warn';
  if (real > target * (1 + MKT_TOLERANCE)) return 'bad';
  return 'ok';
};

const COLUMNS = [
  { key: 'name', label: 'Cliente', align: 'left' },
  { key: 'target', label: 'Target', align: 'right' },
  { key: 'achieved', label: 'Alcanzado', align: 'right' },
  { key: 'pct_vs_target', label: 'vs Target', align: 'right' },
  { key: 'achieved_ly', label: 'Año ant.', align: 'right' },
  { key: 'pct_yoy', label: 'YoY', align: 'right' },
  { key: 'orders', label: 'Órd./Leads', align: 'right' },
  { key: 'avg_ticket', label: 'Ticket', align: 'right' },
  { key: 'conversion_rate', label: 'CR %', align: 'right' },
  { key: 'sessions', label: 'Sesiones', align: 'right' },
  { key: 'spend', label: 'Spend', align: 'right' },
  { key: 'mkt_pct', label: '% Mkt', align: 'right' },
];

/**
 * Tabla de pacing de todas las marcas (home del panel admin), con la estética
 * del reporte (Sora/Manrope, líneas finas). Orden por cualquier columna
 * (desc → asc, nulls al final); click en una fila abre el panel de esa marca.
 * El semáforo de % vs Target y % Mkt va como texto en negrita de color.
 */
export default function BrandsOverviewTable({ start, end, onSelectBrand }) {
  const { data: rows = [], isLoading, isError } = useBrandsOverviewQuery(start, end);
  const [sort, setSort] = useState({ key: 'pct_vs_target', dir: 'desc' });

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const mul = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = a[sort.key];
      const vb = b[sort.key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulls siempre al final
      if (vb == null) return -1;
      if (typeof va === 'string') return va.localeCompare(vb) * mul;
      return (va - vb) * mul;
    });
  }, [rows, sort]);

  const onSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  if (isLoading) return <p className="brandstbl-state" style={{ color: '#5B5F7A' }}>Cargando marcas…</p>;
  if (isError) return <p className="brandstbl-state" style={{ color: '#EF6F6C' }}>No se pudo cargar el resumen.</p>;

  return (
    <div className="brandstbl">
      <div className="brandstbl-scroll">
        <table>
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <SortHeader key={c.key} sortKey={c.key} sort={sort} onSort={onSort} align={c.align}>
                  {c.label}
                </SortHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr key={r.brand_id} onClick={() => onSelectBrand(r.brand_id)}>
                <td>
                  <div className="cliente">
                    <BrandLogo name={r.name} logoUrl={r.logo_url} />
                    <span className="cname">{r.name}</span>
                    <span className="vtag">{r.vertical === 'lead_gen' ? 'leads' : 'ecom'}</span>
                  </div>
                </td>
                <td>
                  {r.vertical === 'lead_gen'
                    ? r.target == null ? '—' : `${Math.round(r.target)} leads`
                    : fmtMoney(r.target, r.currency)}
                </td>
                <td className="alc">
                  {r.vertical === 'lead_gen'
                    ? r.achieved == null ? '—' : `${r.achieved} leads`
                    : fmtMoney(r.achieved, r.currency)}
                </td>
                <td className={pctClass(r.pct_vs_target)}>
                  {r.pct_vs_target == null ? '—' : `${formatMetric(r.pct_vs_target, 'number')}%`}
                </td>
                <td className="muted" title="Mismas fechas del año anterior">
                  {r.vertical === 'lead_gen'
                    ? r.achieved_ly == null ? '—' : `${r.achieved_ly} leads`
                    : fmtMoney(r.achieved_ly, r.currency)}
                </td>
                <td className={yoyClass(r.pct_yoy)}>
                  {r.pct_yoy == null ? '—' : `${r.pct_yoy >= 0 ? '+' : ''}${formatMetric(r.pct_yoy, 'number')}%`}
                </td>
                <td>{r.orders ?? '—'}</td>
                <td>{r.avg_ticket == null ? '—' : `$ ${formatMetric(r.avg_ticket, 'number')}`}</td>
                <td>{r.conversion_rate == null ? '—' : formatMetric(r.conversion_rate, 'ratio').replace('x', '%')}</td>
                <td>{r.sessions == null ? '—' : formatMetric(r.sessions, 'number')}</td>
                <td>{fmtMoney(r.spend, r.currency)}</td>
                <td
                  className={mktClass(r.mkt_pct, r.mkt_pct_target)}
                  title={r.mkt_pct_target != null ? `objetivo ${formatMetric(r.mkt_pct_target, 'number')}%` : undefined}
                >
                  {r.mkt_pct == null ? '—' : `${formatMetric(r.mkt_pct, 'ratio').replace('x', '')}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <p className="brandstbl-empty">Sin marcas configuradas.</p>}
    </div>
  );
}
